import { useEffect, useState } from "react";
import axios from "axios";
import { formatCurrency } from "@/utils/formatCurrency";
import { getBillingSubscriptions } from "../api/getBillingSubscriptions/getBillingSubscriptionsApi";
import { BillingSubscription } from "../api/getBillingSubscriptions/types";
import { getBillingMetadata } from "../api/getBillingMetadata/getBillingMetadataApi";
import { BillingMetadata } from "../api/getBillingMetadata/types";
import { BillingSortAccessors } from "./useBillingClientSorting";
import { useBillingClientTable } from "./useBillingClientTable";
import { formatBillingTrade } from "../utils/billingFormatters";

const FREQUENCY_ORDER: Record<string, number> = {
  weekly: 1,
  monthly: 2,
  quarterly: 3,
  yearly: 4,
  once: 5,
};

const SORT_ACCESSORS: BillingSortAccessors<BillingSubscription> = {
  productName: (subscription) => subscription.productName,
  trade: (subscription) =>
    subscription.trade && formatBillingTrade(subscription.trade),
  frequency: (subscription) =>
    subscription.frequency &&
    (FREQUENCY_ORDER[subscription.frequency] ?? subscription.frequency),
  startDate: (subscription) => subscription.startDate,
  lastInvoice: (subscription) => subscription.lastInvoice?.invoiceDate ?? null,
  status: (subscription) => subscription.status,
};

function describePastDueInvoices(count: number): string {
  if (count === 0) return "Nothing past due";

  return count === 1 ? "1 invoice past due" : `${count} invoices past due`;
}

function describePausedSubscriptions(count: number): string | undefined {
  if (count === 0) return undefined;

  return `${count} with payments paused`;
}

export function useBillingSubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState<BillingSubscription[]>([]);
  const [metadata, setMetadata] = useState<BillingMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  const {
    sorting,
    handleSortingChange,
    pageIndex,
    pageSize,
    pageRows,
    handlePageChange,
  } = useBillingClientTable(subscriptions, SORT_ACCESSORS);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      getBillingSubscriptions(controller.signal),
      getBillingMetadata(controller.signal),
    ])
      .then(([subscriptionsResponse, metadataResponse]) => {
        setSubscriptions(subscriptionsResponse.data);
        setMetadata(metadataResponse.data);
        setLoading(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const summary = metadata && {
    balanceDue: formatCurrency(metadata.balanceDue),
    balanceDueDetail: describePastDueInvoices(metadata.pastDueInvoiceCount),
    activeSubscriptions: String(metadata.activeSubscriptionCount),
    activeSubscriptionsDetail: describePausedSubscriptions(
      metadata.pausedSubscriptionCount,
    ),
  };

  return {
    loading,
    summary,
    sorting,
    handleSortingChange,
    pageRows,
    totalCount: subscriptions.length,
    pageIndex,
    pageSize,
    handlePageChange,
  };
}
